import { __callbacks } from "Utils/Callbacks/Callback";
import { ChatMessageFragment } from "Utils/Chats/ChatMessageFragment";
import { Chat } from "Utils/Enums";
import { useCallback, useEffect, useState } from "react";
import styles from "./FileUpload.module.scss";
import { __utils } from "Utils/Utils";
import { __map } from "Utils/Map/Map";
import { __sb } from "Utils/SB/sb";

const CIRCLE_DASH_ARRAY = 53.06292724609375;

export default function FileUpload(props: any) {
    const [id,] = useState(props.id ?? __utils.getUuidv4());
    const [progressPercentage, setProgressPercentage] = useState<number>(() => {
        return (props.hasOwnProperty("uploadFailed") && !props.uploadFailed) ? (100) : (0)
    });
    const [dashOffset, setDashOffset] = useState<number>(CIRCLE_DASH_ARRAY);

    const [uploadFailed, setUploadFailed] = useState<boolean>(props.uploadFailed || false);
    const [uploadedFileLink, setUploadedFileLink] = useState<string>(props.uploadedFileLink || "#0");
    const [uploadedFileLinkTarget, setUploadedFileLinkTarget] = useState<string>(() => {
        return (props.uploadedFileLink && props.uploadedFileLink !== "#0") ? ("_blank") : ("")
    });

    useEffect(() => {
        if (props.local) return;

        // trigger file upload updates
        __callbacks.fileUploadUpdate.trigger(
            {
                type: Chat.Fragment,
                data: new ChatMessageFragment({
                    id,
                    chatId: props.conversationId,
                    messageNo: props.messageNo,
                    messageType: props.messageType,
                    fragmentType: "FileUpload",
                    time: Date.now(),
                    props: {
                        id,
                        conversationId: props.conversationId,
                        messageNo: props.messageNo,
                        messageType: props.messageType,
                        fileName: props.fileName,
                        fileSize: props.fileSize,
                        fileType: props.fileType,
                        type: "FileUpload",
                        uploadFailed: uploadFailed,
                        uploadedFileLink: uploadedFileLink
                    }
                })
            }
        );
    }, [props, uploadFailed, uploadedFileLink, id]);

    const handleFileUpload = useCallback(
        (file: File) => {
            __sb.sendFile(file, props.conversationId, setUploadedFileLink);
            setProgressPercentage(100);
        },
        [props.conversationId],
    );

    useEffect(() => {
        if (props.local) return;
        handleFileUpload(props.fileData);
    }, [props.local, props.fileData, handleFileUpload]);

    useEffect(() => {
        setDashOffset(
            CIRCLE_DASH_ARRAY - (CIRCLE_DASH_ARRAY * (progressPercentage / 100))
        );
    }, [progressPercentage]);

    return (
        <>
            <a href={uploadedFileLink} rel="noreferrer" target={uploadedFileLinkTarget}>
                <div className={styles.container}>
                    <div className={`${styles["container-icon"]} config-icon-file-upload-download`}>
                        {
                            (uploadFailed) ?
                                (<div className={styles["container-upload-failed"]}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="17" viewBox="0 0 18 17" fill="none"><path d="M10.1347 12.6634C10.1347 13.3242 9.69166 13.8175 9.00705 13.8175H8.98326C8.32245 13.8175 7.86661 13.3242 7.86661 12.6634C7.86661 11.9797 8.33613 11.5102 9.00705 11.5102C9.67798 11.5102 10.1228 11.9779 10.1347 12.6634ZM8.28488 10.8769H9.73194L10.0084 5.13454H8.00572L8.28488 10.8769ZM17.8337 15.7231C17.6242 16.0664 17.2516 16.2778 16.8508 16.2778H1.1505C0.735876 16.2778 0.353292 16.0545 0.148323 15.6939C-0.0539294 15.3333 -0.0493391 14.8931 0.162094 14.5371L8.04151 0.558326C8.24835 0.209653 8.62269 0 9.02719 0H9.0583C9.4747 0.0100236 9.84904 0.245251 10.0431 0.613222L17.8667 14.5928C18.0534 14.9488 18.0452 15.3763 17.8337 15.7231ZM16.8508 15.1236L9.02626 1.1486L1.15144 15.1236H16.8508Z" fill="#e91e63"></path></svg>
                                    <div className="">Failed</div>
                                </div>) :
                                (
                                    <>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                                            {
                                                (progressPercentage === 100) ?
                                                    (<path d="M9.5 5C9.5 4.72386 9.27614 4.5 9 4.5C8.72386 4.5 8.5 4.72386 8.5 5H9.5ZM8.64645 13.3536C8.84171 13.5488 9.15829 13.5488 9.35355 13.3536L12.5355 10.1716C12.7308 9.97631 12.7308 9.65973 12.5355 9.46447C12.3403 9.2692 12.0237 9.2692 11.8284 9.46447L9 12.2929L6.17157 9.46447C5.97631 9.2692 5.65973 9.2692 5.46447 9.46447C5.2692 9.65973 5.2692 9.97631 5.46447 10.1716L8.64645 13.3536ZM8.5 5V13H9.5V5H8.5Z" fill="black" />) :
                                                    (<></>)
                                            }
                                            <circle cx="9" cy="9" r="8.5" stroke="black" strokeDasharray={53.06292724609375} strokeDashoffset={dashOffset} className={`config-icon-file-upload-download`} />
                                        </svg>
                                        {
                                            (progressPercentage < 100) ?
                                                (<div className={styles["progress"]}>{progressPercentage}%</div>) :
                                                (<></>)
                                        }
                                    </>
                                )
                        }

                    </div>
                    <div className={`${styles["container-file-stats"]} config-icon-file-upload-download`}>
                        <div className={styles["icon-file"]}>
                            <svg xmlns="http://www.w3.org/2000/svg" fill="white" viewBox="0 0 24 24" width="24px" height="24px"><path d="M 6 2 C 4.9057453 2 4 2.9057453 4 4 L 4 20 C 4 21.094255 4.9057453 22 6 22 L 18 22 C 19.094255 22 20 21.094255 20 20 L 20 8 L 14 2 L 6 2 z M 6 4 L 13 4 L 13 9 L 18 9 L 18 20 L 6 20 L 6 4 z" /></svg>
                        </div>
                        <div className={styles["file-name"]}>{props.fileName}</div>
                    </div>
                </div>
            </a>
        </>
    )
}
